import axios from "axios";
import * as Sentry from "@sentry/vue";

// The backend (hawk/controllers/api_carpool.php) never calls http_response_code(),
// so every error response — including 401 session-expiry — leaves the server as
// HTTP 200. The real status sits in the body as `code`, and on error the `data`
// key holds { error, message } instead of the payload callers expect.
//
// Without this interceptor a caller destructures the error object as if it were
// success data and blows up somewhere unrelated: Sentry CARPOOLING-R
// (`a.reduce is not a function`), CARPOOLING-4B (`state.name` is null), and the
// whole `reading 'data'` cluster are all this one bug.
//
// This instance turns those fake successes into real rejections carrying the
// server's own message. Note that store/index.js API_CALL deliberately catches
// and re-resolves, so its ~200 existing callers keep today's semantics.
const api = axios.create();

export const SESSION_EXPIRED_MESSAGES = [
  "Please login first.",
  "Token not found or you session has ended.",
  "Your session has ended, please relogin.",
];

export function isSessionExpired(error) {
  if (!error) return false;
  if (error.apiCode === 401) return true;
  return SESSION_EXPIRED_MESSAGES.indexOf(error.message) !== -1;
}

// An expired session is expected, not a defect — reporting it only buries the
// real failures. Everything else keeps its server-supplied message, so Sentry
// groups on the actual cause instead of whatever destructure crashed first.
export function reportApiError(error, method) {
  if (isSessionExpired(error)) return;
  Sentry.captureException(error, { tags: { api_method: method } });
}

api.interceptors.response.use(
  (response) => {
    const body = response.data;
    const code = body && body.code;

    if (code >= 400) {
      const payload = body.data || {};
      const error = new Error(payload.message || `API error ${code}`);
      error.apiCode = code;
      error.response = response;
      return Promise.reject(error);
    }

    return response;
  },
  (error) => {
    // Reached when the transport itself fails, and — once resp_json starts
    // calling http_response_code() — for every server-reported error too. axios
    // rejects before the handler above runs, so without this the error would
    // arrive as "Request failed with status code 401" carrying no apiCode, and
    // isSessionExpired() would stop recognising an expired session.
    const body = error.response && error.response.data;
    const code = (body && body.code) || (error.response && error.response.status);

    // No status at all means a network failure, timeout or abort. Those are a
    // different kind of problem and keep their own identity in Sentry.
    if (!code) return Promise.reject(error);

    const payload = (body && body.data) || {};
    const normalized = new Error(payload.message || error.message);
    normalized.apiCode = code;
    normalized.response = error.response;
    return Promise.reject(normalized);
  }
);

export default api;
